'use client';

/**
 * UNIVERS DU SAVOIR — Explorer l'Univers avec 2500 produits.
 * Décor cosmique (étoiles scintillantes, planètes flottantes) + explorateur complet :
 * recherche, 20 domaines, filtre par marque, tri par prix, pagination (48/page),
 * modale de détail avec couverture procédurale ou photo réelle.
 */
import { useEffect, useMemo, useState } from 'react';
import Image from 'next/image';
import { useApp } from '@/lib/store';
import { DICTS } from '@/lib/i18n';
import PageBanner from './PageBanner';
import ProductCover from '@/components/quran/ProductCover';
import { cn } from '@/lib/utils';
import {
  Search, Sparkles, ChevronRight, ChevronLeft, X, ShoppingBag, Gem,
} from 'lucide-react';

interface Product {
  id: string;
  cat: string;
  brand: string;
  label: string;
  price: number;
  hue: number;
  pat: number;
  real: boolean;
}

const PAGE_SIZE = 48;
const CAT_ORDER = [
  'coran', 'livres_savoir', 'mushaf_num', 'medias_audio', 'medias_video', 'cours_conferences',
  'art_calligraphie', 'names_special', 'parfums', 'encens_huiles', 'cosmetique',
  'dattes_miel', 'eaux', 'koran_super', 'restaurants', 'poulets', 'viandes',
  'tapis_priere', 'tenues_priere', 'pelerinage', 'enfants',
];

export default function UniversSection() {
  const { uiLang } = useApp();
  const t = DICTS[uiLang];
  const [items, setItems] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState('all');
  const [brand, setBrand] = useState('all');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<'default' | 'asc' | 'desc'>('default');
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState<Product | null>(null);

  useEffect(() => {
    let alive = true;
    fetch('/api/products')
      .then((r) => r.json())
      .then((d: Product[]) => { if (alive) { setItems(Array.isArray(d) ? d : []); setLoading(false); } })
      .catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const cats = useMemo(() => {
    const counts = new Map<string, number>();
    items.forEach((p) => counts.set(p.cat, (counts.get(p.cat) || 0) + 1));
    const keys = [...counts.keys()].sort((a, b) => {
      const ia = CAT_ORDER.indexOf(a), ib = CAT_ORDER.indexOf(b);
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    });
    return keys.map((k) => ({ key: k, count: counts.get(k) || 0 }));
  }, [items]);

  const brands = useMemo(
    () => Array.from(new Set(items.filter((p) => p.cat === cat).map((p) => p.brand))).sort((a, b) => a.localeCompare(b)),
    [items, cat]
  );

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let out = items.filter((p) =>
      (cat === 'all' || p.cat === cat) &&
      (brand === 'all' || p.brand === brand) &&
      (!needle || p.label.toLowerCase().includes(needle) || p.brand.toLowerCase().includes(needle))
    );
    if (sort === 'asc') out = [...out].sort((a, b) => a.price - b.price);
    if (sort === 'desc') out = [...out].sort((a, b) => b.price - a.price);
    return out;
  }, [items, cat, brand, q, sort]);

  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pages);
  const visible = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  // Retour à la page 1 quand les filtres changent (ajustement d'état pendant le rendu)
  const [filterKey, setFilterKey] = useState('');
  const currentKey = `${cat}|${brand}|${q}|${sort}`;
  if (filterKey !== currentKey) {
    setFilterKey(currentKey);
    if (page !== 1) setPage(1);
  }

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setDetail(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const fmtPrice = (n: number) =>
    new Intl.NumberFormat(uiLang === 'en' ? 'en-GB' : 'fr-FR').format(n) + ' FCFA';

  const imgSrc = (p: Product) => `/images/products/${p.id}`;
  const srcLabel = (p: Product) => `${t.univers.domain}: ${t.univers.cats[p.cat as keyof typeof t.univers.cats] ?? p.cat}`;

  /* ===== Décor étoilé déterministe ===== */
  const stars = useMemo(
    () => Array.from({ length: 46 }, (_, i) => {
      const s = (i * 7919) % 1000;
      return {
        left: (s % 100),
        top: ((s * 7) % 100),
        size: 1 + (s % 3) * 0.6,
        delay: (s % 34) / 10,
      };
    }),
    []
  );

  return (
    <section className="cosmic-bg relative min-h-screen py-12" aria-label={t.univers.title}>
      {/* Étoiles scintillantes */}
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        {stars.map((s, i) => (
          <span key={i} className="twinkle absolute rounded-full bg-[#f7e7b0]"
            style={{ left: `${s.left}%`, top: `${s.top}%`, width: s.size, height: s.size, animationDelay: `${s.delay}s` }} />
        ))}
      </div>

      {/* Planètes ornementales flottantes */}
      <div aria-hidden className="pointer-events-none absolute -left-16 top-40 opacity-40">
        <div className="float-soft-slow relative h-40 w-40 rounded-full"
          style={{ background: 'radial-gradient(circle at 35% 30%, #2e8b6e, #032019 70%)', boxShadow: '0 0 60px rgba(46,139,110,0.35), inset -18px -14px 40px rgba(0,0,0,0.55)' }}>
          <div className="spin-slow absolute left-1/2 top-1/2 h-[190%] w-[190%] -translate-x-1/2 -translate-y-1/2 rounded-[50%] border border-[rgba(212,175,55,0.35)]"
            style={{ transform: 'translate(-50%,-50%) rotateX(72deg)' }} />
        </div>
      </div>
      <div aria-hidden className="pointer-events-none absolute -right-10 top-[55%] hidden opacity-35 lg:block">
        <div className="float-soft relative h-56 w-56 rounded-full"
          style={{ background: 'radial-gradient(circle at 32% 28%, #d4af37, #3a2c05 60%, #0b0902 80%)', boxShadow: '0 0 80px rgba(212,175,55,0.25), inset -24px -20px 60px rgba(0,0,0,0.6)' }} />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
        {/* ============ Bannière dorée + badge produits ============ */}
        <PageBanner
          src="/images/banners/gallery_v2.png"
          eyebrow={`NDIAMSYL PEYE — ${t.univers.eyebrow}`}
          title={t.univers.title}
          sub={t.univers.subtitle}
        />
        <div className="mt-8 mb-6 text-center">
          <div className="inline-flex items-center gap-3 rounded-full border border-[rgba(212,175,55,0.4)] bg-[rgba(212,175,55,0.08)] px-6 py-2.5">
            <Gem className="h-4 w-4 text-[#d4af37]" aria-hidden />
            <span className="font-serif-royal text-2xl font-bold text-[#f0d98c]">
              {loading ? '…' : new Intl.NumberFormat().format(items.length)}
            </span>
            <span className="text-xs font-semibold uppercase tracking-widest text-[#cfe0cf]/70">{t.univers.products}</span>
          </div>
        </div>

        {/* ============ Contrôles ============ */}
        <div className="glass-gold mb-6 rounded-2xl p-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#d4af37]/70" aria-hidden />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t.univers.search_placeholder}
                className="w-full rounded-xl border border-[rgba(212,175,55,0.35)] bg-[rgba(2,25,19,0.8)] py-2.5 pl-9 pr-3 text-sm text-[#f5f0e1] placeholder:text-[#cfe0cf]/40 outline-none focus:border-[#d4af37]"
                aria-label={t.univers.search_placeholder}
              />
            </div>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value as typeof sort)}
              className="rounded-xl border border-[rgba(212,175,55,0.35)] bg-[rgba(2,25,19,0.8)] px-3 py-2.5 text-sm font-semibold text-[#f0d98c] outline-none focus:border-[#d4af37]"
              aria-label={t.univers.sort}
            >
              <option value="default">{t.univers.sort_default}</option>
              <option value="asc">{t.univers.sort_asc}</option>
              <option value="desc">{t.univers.sort_desc}</option>
            </select>
            {brands.length > 0 && (
              <select
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
                className="rounded-xl border border-[rgba(212,175,55,0.35)] bg-[rgba(2,25,19,0.8)] px-3 py-2.5 text-sm font-semibold text-[#f0d98c] outline-none focus:border-[#d4af37]"
                aria-label={t.univers.brand_filter}
              >
                <option value="all">{t.univers.all_brands} ({brands.length})</option>
                {brands.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            )}
          </div>

          {/* Domaines */}
          <div className="mt-3 flex flex-wrap justify-center gap-1.5 gold-scroll max-h-36 overflow-y-auto pt-1">
            <button
              onClick={() => { setCat('all'); setBrand('all'); }}
              className={cn(
                'rounded-full border px-3.5 py-1.5 text-[11px] font-bold transition-all',
                cat === 'all'
                  ? 'border-[#d4af37] bg-[#d4af37] text-[#032019]'
                  : 'border-[rgba(212,175,55,0.3)] text-[#cfe0cf]/75 hover:border-[rgba(212,175,55,0.6)] hover:text-[#f0d98c]'
              )}
            >
              {t.univers.all} ({items.length})
            </button>
            {cats.map((c) => (
              <button
                key={c.key}
                onClick={() => { setCat(c.key); setBrand('all'); }}
                className={cn(
                  'rounded-full border px-3.5 py-1.5 text-[11px] font-bold transition-all',
                  cat === c.key
                    ? 'border-[#d4af37] bg-[#d4af37] text-[#032019]'
                    : 'border-[rgba(212,175,55,0.3)] text-[#cfe0cf]/75 hover:border-[rgba(212,175,55,0.6)] hover:text-[#f0d98c]'
                )}
              >
                {t.univers.cats[c.key as keyof typeof t.univers.cats] ?? c.key} ({c.count})
              </button>
            ))}
          </div>
        </div>

        {/* ============ Grille produits ============ */}
        {loading ? (
          <div className="grid grid-cols-2 gap-3 py-10 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
            {Array.from({ length: 12 }, (_, i) => (
              <div key={i} className="aspect-square animate-pulse rounded-xl border border-[rgba(212,175,55,0.15)] bg-[rgba(7,53,40,0.4)]" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
            {visible.map((p) => (
              <button
                key={p.id}
                onClick={() => setDetail(p)}
                className="group relative aspect-square overflow-hidden rounded-xl border border-[rgba(212,175,55,0.25)] transition-all hover:border-[rgba(212,175,55,0.75)] hover:shadow-[0_0_28px_rgba(212,175,55,0.3)]"
                aria-label={`${p.label} — ${p.brand}`}
              >
                {p.real ? (
                  <Image src={imgSrc(p)} alt={p.label} fill unoptimized
                    sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 16vw"
                    className="object-cover transition-transform duration-500 group-hover:scale-105" />
                ) : (
                  <span className="block h-full w-full transition-transform duration-500 group-hover:scale-105">
                    <ProductCover id={p.id} spec={{ hue: p.hue, pat: p.pat }} label={p.label} brand={p.brand} className="h-full w-full" />
                  </span>
                )}
                <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[rgba(2,25,19,0.95)] to-transparent px-2 pb-1.5 pt-6 text-left">
                  <span className="block truncate text-[10px] font-bold text-[#f0d98c]">{p.label}</span>
                  <span className="flex items-center justify-between gap-1">
                    <span className="truncate text-[9px] uppercase tracking-wider text-[#cfe0cf]/60">{p.brand}</span>
                    <span className="shrink-0 text-[9px] font-bold text-[#7fd6b4]">{fmtPrice(p.price)}</span>
                  </span>
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Pagination */}
        {!loading && pages > 1 && (
          <div className="mt-8 flex flex-wrap items-center justify-center gap-1.5">
            <button
              onClick={() => setPage(Math.max(1, safePage - 1))}
              disabled={safePage === 1}
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-[rgba(212,175,55,0.35)] text-[#f0d98c] transition-colors hover:bg-[rgba(212,175,55,0.12)] disabled:opacity-30"
              aria-label={t.univers.prev}
            >
              <ChevronLeft className="h-4 w-4 rtl-flip" aria-hidden />
            </button>
            {Array.from({ length: Math.min(pages, 9) }, (_, i) => {
              let pno: number;
              if (pages <= 9) pno = i + 1;
              else if (safePage <= 5) pno = i + 1;
              else if (safePage >= pages - 4) pno = pages - 8 + i;
              else pno = safePage - 4 + i;
              return (
                <button
                  key={pno}
                  onClick={() => setPage(pno)}
                  className={cn(
                    'h-9 min-w-9 rounded-lg border px-2 text-[13px] font-bold transition-all',
                    pno === safePage
                      ? 'border-[#d4af37] bg-[#d4af37] text-[#032019]'
                      : 'border-[rgba(212,175,55,0.3)] text-[#cfe0cf]/75 hover:border-[rgba(212,175,55,0.6)] hover:text-[#f0d98c]'
                  )}
                >
                  {pno}
                </button>
              );
            })}
            <button
              onClick={() => setPage(Math.min(pages, safePage + 1))}
              disabled={safePage === pages}
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-[rgba(212,175,55,0.35)] text-[#f0d98c] transition-colors hover:bg-[rgba(212,175,55,0.12)] disabled:opacity-30"
              aria-label={t.univers.next}
            >
              <ChevronRight className="h-4 w-4 rtl-flip" aria-hidden />
            </button>
            <span className="ml-2 text-xs text-[#cfe0cf]/55">
              {safePage} / {pages} — {filtered.length} {t.univers.results}
            </span>
          </div>
        )}
      </div>

      {/* ============ Modale détail produit ============ */}
      {detail && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-[rgba(1,15,11,0.94)] p-4 backdrop-blur-sm"
          onClick={() => setDetail(null)}
          role="dialog"
          aria-modal="true"
          aria-label={detail.label}
        >
          <div className="glass-gold relative w-full max-w-lg rounded-2xl p-5" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setDetail(null)}
              className="absolute -top-3 right-3 flex h-9 w-9 items-center justify-center rounded-full border border-[rgba(212,175,55,0.5)] bg-[rgba(3,32,25,0.95)] text-[#f0d98c] hover:bg-[rgba(212,175,55,0.2)]"
              aria-label={t.common.close}
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
            <div className="flex flex-col gap-4 sm:flex-row">
              <div className="relative h-44 w-44 shrink-0 self-center overflow-hidden rounded-xl border border-[rgba(212,175,55,0.45)] sm:self-start">
                {detail.real ? (
                  <Image src={imgSrc(detail)} alt={detail.label} fill unoptimized className="object-cover" />
                ) : (
                  <ProductCover id={detail.id} spec={{ hue: detail.hue, pat: detail.pat }} label={detail.label} brand={detail.brand} className="h-full w-full" />
                )}
              </div>
              <div className="min-w-0 flex-1 text-center sm:text-left">
                <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-[#d4af37]/75">
                  {srcLabel(detail)}
                </p>
                <h3 className="font-serif-royal mt-1 text-2xl font-bold text-[#f5f0e1]">{detail.label}</h3>
                <p className="mt-0.5 text-xs font-bold tracking-widest text-[#cfe0cf]/60 uppercase">{detail.brand}</p>
                <p className="font-serif-royal mt-3 text-2xl font-bold text-[#7fd6b4]">{fmtPrice(detail.price)}</p>
                <p className="mt-3 text-[11px] leading-relaxed text-[#cfe0cf]/55">
                  {t.univers.ref}: NDIAMSYL PEYE · {detail.id.split('/').pop()?.replace(/\.[a-z]+$/, '')}
                </p>
                <button
                  className="gold-glow-hover mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-b from-[#e8c86a] to-[#b8933a] px-5 py-2.5 text-sm font-bold text-[#032019] sm:w-auto"
                  onClick={(e) => e.preventDefault()}
                >
                  <ShoppingBag className="h-4 w-4" aria-hidden />
                  {t.univers.order}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
